import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  FormControlLabel,
  IconButton,
  MenuItem,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material'
import { ArrowForward } from '@mui/icons-material'
import { useEffect, useMemo, useState } from 'react'
import type { CatalogDraft, CatalogRow } from 'src/CatalogList/CatalogList.types'
import './CatalogList.css'
import dayjs from 'dayjs'
import { DatePicker } from '@impres-development/assets/client'

const CONTRACT_TYPE_OPTIONS = ['GWAC', 'BPA', 'IDIQ']
const PRIME_SUB_JV_OPTIONS = ['Prime', 'Sub', 'JV']
const BUSINESS_SIZE_OPTIONS = ['Small', 'Large']
const SET_ASIDE_OPTIONS = ['HUBZone', 'SDVOSB', 'WOSB', 'EDWOSB', 'None']

export interface AddCatalogSidebarProps {
  open: boolean
  onClose: () => void
  onCreate: (draft: CatalogDraft) => Promise<void>
  parentLevelContracts: CatalogRow[]
}

const EMPTY_DRAFT: CatalogDraft = {
  contractVehicle: '',
  contractNumber: '',
  contractName: '',
  status: 'Active',
  contractType: '',
  primeSubJV: '',
  businessSize: '',
  setAside: '',
  scope: '',
  popStart: '',
  popEnd: '',
  contractFee: 0,
  contractFeeComments: '',
  partnerFee: 0,
  partnerFeeComments: '',
  generalComments: '',
  parentId: null,
}

export function AddCatalogSidebar({ open, onClose, onCreate, parentLevelContracts }: AddCatalogSidebarProps) {
  const [draft, setDraft] = useState<CatalogDraft>(EMPTY_DRAFT)

  useEffect(() => {
    if (!open) return
    setDraft(EMPTY_DRAFT)
  }, [open])

  const set = (key: keyof CatalogDraft) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setDraft(d => ({ ...d, [key]: e.target.value }))

  const setPercent = (key: keyof CatalogDraft) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setDraft(d => ({ ...d, [key]: e.target.value === '' ? 0 : Number(e.target.value) / 100 }))

  const percentValue = (value: number) => Math.round(Number(value) * 10000) / 100

  const canSave = useMemo(
    () => draft.contractVehicle.trim().length > 0 && draft.contractNumber.trim().length > 0,
    [draft.contractVehicle, draft.contractNumber],
  )

  const hasUnsavedChanges = useMemo(
    () => (Object.keys(draft) as (keyof CatalogDraft)[]).some(k => draft[k] !== EMPTY_DRAFT[k]),
    [draft],
  )
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false)

  const handleRequestClose = () => {
    if (hasUnsavedChanges) {
      setConfirmCloseOpen(true)
    } else {
      onClose()
    }
  }

  return (
    <Drawer anchor="right" open={open} onClose={handleRequestClose} className="catalog-drawer">
      <div className="catalog-drawer-header">
        <Typography variant="h6" className="catalog-drawer-header-title">
          Create New Catalog
        </Typography>
        <div className="catalog-drawer-header-actions">
          <IconButton size="small" onClick={handleRequestClose} aria-label="Close drawer">
            <ArrowForward />
          </IconButton>
        </div>
      </div>
      <Divider />
      <Tabs value={0} className="catalog-drawer-tabs">
        <Tab label="EDIT FIELDS" />
        <Tab label="CTA OEM" disabled />
      </Tabs>
      <Divider />
      <div className="catalog-drawer-content">
        <div className="catalog-drawer-form">
          <div className="catalog-drawer-fields">
            <TextField
              label="Contract Vehicle *"
              size="small"
              select
              value={draft.parentId ?? ''}
              onChange={e => {
                const parent = parentLevelContracts.find(p => String(p.id) === String(e.target.value))
                setDraft(d => ({
                  ...d,
                  parentId: parent?.id ?? null,
                  contractVehicle: parent?.contractVehicle ?? '',
                }))
              }}
              fullWidth
            >
              <MenuItem value="">
                <em>Select</em>
              </MenuItem>
              {parentLevelContracts.map(p => (
                <MenuItem key={p.id} value={p.id}>
                  {p.contractVehicle}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Contract # *"
              size="small"
              value={draft.contractNumber}
              onChange={set('contractNumber')}
              fullWidth
            />
            <TextField
              label="Contract Name"
              size="small"
              value={draft.contractName}
              onChange={set('contractName')}
              fullWidth
            />
            <TextField
              label="Contract Type"
              size="small"
              select
              value={draft.contractType}
              onChange={set('contractType')}
              fullWidth
            >
              <MenuItem value="">
                <em>Select</em>
              </MenuItem>
              {CONTRACT_TYPE_OPTIONS.map(o => (
                <MenuItem key={o} value={o}>
                  {o}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Prime/Sub/JV"
              size="small"
              select
              value={draft.primeSubJV}
              onChange={set('primeSubJV')}
              fullWidth
            >
              <MenuItem value="">
                <em>Select</em>
              </MenuItem>
              {PRIME_SUB_JV_OPTIONS.map(o => (
                <MenuItem key={o} value={o}>
                  {o}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Business Size"
              size="small"
              select
              value={draft.businessSize}
              onChange={set('businessSize')}
              fullWidth
            >
              <MenuItem value="">
                <em>Select</em>
              </MenuItem>
              {BUSINESS_SIZE_OPTIONS.map(o => (
                <MenuItem key={o} value={o}>
                  {o}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Set Aside"
              size="small"
              select
              value={draft.setAside}
              onChange={set('setAside')}
              fullWidth
            >
              <MenuItem value="">
                <em>Select</em>
              </MenuItem>
              {SET_ASIDE_OPTIONS.map(o => (
                <MenuItem key={o} value={o}>
                  {o}
                </MenuItem>
              ))}
            </TextField>

            <TextField label="Scope" size="small" value={draft.scope} onChange={set('scope')} fullWidth />
            <DatePicker
              label="PoP Start"
              size="small"
              fullWidth
              value={draft.popStart ? dayjs(draft.popStart) : null}
              onChange={date => setDraft(d => ({ ...d, popStart: date ? date.format('YYYY-MM-DD') : '' }))}
            />
            <DatePicker
              label="PoP End"
              size="small"
              fullWidth
              value={draft.popEnd ? dayjs(draft.popEnd) : null}
              onChange={date => setDraft(d => ({ ...d, popEnd: date ? date.format('YYYY-MM-DD') : '' }))}
            />
            <TextField
              label="Contract Fee (%)"
              type="number"
              size="small"
              value={percentValue(draft.contractFee)}
              onChange={setPercent('contractFee')}
              fullWidth
            />
            <TextField
              label="Contract Fee Comments"
              size="small"
              value={draft.contractFeeComments}
              onChange={set('contractFeeComments')}
              fullWidth
            />
            <TextField
              label="Partner Fee (%)"
              type="number"
              size="small"
              value={percentValue(draft.partnerFee)}
              onChange={setPercent('partnerFee')}
              fullWidth
            />
            <TextField
              label="Partner Fee Comments"
              size="small"
              value={draft.partnerFeeComments}
              onChange={set('partnerFeeComments')}
              fullWidth
            />
            <TextField
              label="General Comments"
              size="small"
              multiline
              minRows={3}
              value={draft.generalComments}
              onChange={set('generalComments')}
              fullWidth
            />

            <FormControlLabel
              label="Active"
              control={
                <Checkbox
                  checked={draft.status === 'Active'}
                  onChange={e => setDraft(d => ({ ...d, status: e.target.checked ? 'Active' : 'Inactive' }))}
                />
              }
            />
          </div>
          <div className="catalog-drawer-form-actions">
            <Button
              variant="contained"
              disabled={!canSave}
              onClick={() =>
                onCreate({
                  ...draft,
                  contractVehicle: draft.contractVehicle.trim(),
                  contractNumber: draft.contractNumber.trim(),
                  contractName: draft.contractName.trim(),
                })
              }
            >
              CREATE NEW
            </Button>
          </div>
        </div>
      </div>

      <Dialog open={confirmCloseOpen} onClose={() => setConfirmCloseOpen(false)}>
        <DialogTitle variant="h6">Unsaved Changes</DialogTitle>
        <DialogContent>
          <Typography>You have unsaved changes. Are you sure you want to exit without saving?</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={() => {
              setConfirmCloseOpen(false)
              onClose()
            }}
          >
            Discard Changes
          </Button>
          <Button variant="contained" onClick={() => setConfirmCloseOpen(false)}>
            Keep Editing
          </Button>
        </DialogActions>
      </Dialog>
    </Drawer>
  )
}
